import type { ReactNode } from "react";

/* ------------------------------------------------------------------ *
 * THE CARD TABLES' SMALL SHARED PARTS — what حکم (`HokmTable`) and بی‌دل (`HeartsTable`) drew the
 * same way, byte for byte, each from its own copy until session D (2026-10-09): the status pill in
 * your strip, the stacked-cards glyph on the felt's per-hand counter chip, and the reset every table
 * <button> wears. پاسور and شلم import them too. The seat is `TableSeat`; the transient notices are
 * `table-notice`; the hand fan's geometry is `hand-fan`.
 * ------------------------------------------------------------------ */

/** Base reset for every table <button> (preflight is disabled → reset explicitly). */
export const TABLE_BTN = "appearance-none m-0 cursor-pointer";

/** A small stacked-cards glyph — on the felt's per-hand counter chip (حکم's tricks won, بی‌دل's
 *  round), so the per-hand count never reads as the header's numeric MATCH score. */
export function TrickStackIcon({ size = 14 }: { size?: number }): ReactNode {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="currentColor"
      aria-hidden="true"
      style={{ display: "block" }}
    >
      <rect x="3.5" y="7.5" width="12" height="14.5" rx="2" opacity="0.4" />
      <rect x="7" y="4" width="12" height="14.5" rx="2" transform="rotate(7 13 11)" />
    </svg>
  );
}

/** A gold (it is your move) or muted status pill, in your strip above the hand. */
export function StatusPill({
  gold,
  truncate = false,
  still = false,
  children,
}: {
  gold: boolean;
  /** No pulse on the gold pill, for a table held to no infinite animation at all (هفت خبیث, CLAUDE.md's
   *  phone-load rule). Off by default: every other table's pill is exactly as it was. */
  still?: boolean;
  /** May the pill shrink to an ellipsis inside a group capped by its row (حکم's strip, card-41)?
   *  Off by default: every other table's pill is exactly as it was. */
  truncate?: boolean;
  children: ReactNode;
}): ReactNode {
  // Deliberately COMPACT (user request 2026-07-10): «نوبت شماست» is a cue, not a
  // headline — same footprint as the muted variant, gold + pulse carry the urgency.
  // `whitespace-nowrap`: ONE line, always. At 320 px حکم's used to wrap to two beside a long own
  // name, and بی‌دل's «منتظر انتخاب بقیه…» to two at 360–384 px and THREE at 320, where it pushed
  // the strip 66 px past the screen; the strip carries the turn countdown as well — your name
  // truncates instead.
  const fit = truncate ? " min-w-0 truncate" : "";
  return gold ? (
    <span
      className={`m-0 whitespace-nowrap rounded-full border border-secondary/50 bg-secondary/20 px-3 py-1 font-body-md text-body-md text-secondary shadow-lg${still ? "" : " animate-pulse"}${fit}`}
    >
      {children}
    </span>
  ) : (
    <span
      className={`m-0 whitespace-nowrap rounded-full border border-outline-variant bg-surface-container/70 px-3.5 py-1 font-body-md text-body-md text-on-surface-variant${fit}`}
    >
      {children}
    </span>
  );
}
